import type { ComponentType, ReactNode } from "react";
import { Icon } from "../Icons/Icon";
import { useMarkDialogDirty } from "../Dialog/FormDirtyContext";

interface SegmentOption {
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  startContent?: ReactNode;
}

interface SegmentControlProps {
  options: SegmentOption[];
  active: string;
  onChange: (id: string) => void;
  /** "default" matches Button height and border style. "large" is the bigger
   *  variant. "compact" is denser (smaller padding + text) for control-bar rows. */
  size?: "default" | "large" | "compact";
  disabled?: boolean;
  /** Each option button gets `${id}-option-${option.id}`. */
  "data-testid"?: string | undefined;
}

export function SegmentControl({
  options,
  active,
  onChange,
  size = "default",
  disabled,
  "data-testid": dataTestid,
}: SegmentControlProps) {
  const markDirty = useMarkDialogDirty();

  function handleChange(id: string) {
    markDirty?.();
    onChange(id);
  }

  if (size === "large") {
    return (
      <div
        className={`flex rounded-lg bg-muted p-1 ${disabled ? "pointer-events-none opacity-40" : ""}`}
      >
        {options.map((opt) => {
          const isActive = active === opt.id;
          return (
            <button
              type="button"
              key={opt.id}
              disabled={disabled}
              onClick={() => handleChange(opt.id)}
              {...(dataTestid !== undefined && {
                "data-testid": `${dataTestid}-option-${opt.id}`,
              })}
              className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-all ${
                isActive
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {opt.startContent}
              {opt.icon && (
                <Icon
                  icon={opt.icon}
                  size="lg"
                  {...(isActive ? { color: "text-brand-text" } : {})}
                  weight={isActive ? "duotone" : "regular"}
                />
              )}
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={`inline-flex overflow-hidden rounded-md border border-border-strong ${disabled ? "pointer-events-none opacity-40" : ""}`}
    >
      {options.map((opt, i) => {
        const isActive = active === opt.id;
        return (
          <button
            type="button"
            key={opt.id}
            disabled={disabled}
            onClick={() => handleChange(opt.id)}
            {...(dataTestid !== undefined && {
              "data-testid": `${dataTestid}-option-${opt.id}`,
            })}
            className={`inline-flex items-center justify-center gap-1.5 ${size === "compact" ? "px-2.5 py-0.5 text-[11px]" : "px-3 py-1.5 text-xs"} font-bold whitespace-nowrap transition-colors ${
              i > 0 ? "border-l border-border-strong" : ""
            } ${
              isActive
                ? "bg-brand text-white"
                : "bg-card text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            {opt.startContent}
            {opt.icon && (
              <Icon
                icon={opt.icon}
                size="sm"
                {...(isActive ? { color: "text-white" } : {})}
                weight={isActive ? "duotone" : "regular"}
              />
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
